import React, { useEffect, useRef, memo } from 'react'

interface TradingViewWidgetProps {
  symbol?: string
  theme?: string
  height?: number
  interval?: string
  toolbar_bg?: string
  style?: string
  locale?: string
  timezone?: string
  enable_publishing?: boolean
  allow_symbol_change?: boolean
}

const TradingViewWidget: React.FC<TradingViewWidgetProps> = ({
  symbol,
  theme = 'dark',
  height = 500,
  interval = 'D',
  toolbar_bg = '#0f172a',
  style = '1',
  locale = 'en',
  timezone = 'Asia/Kolkata',
  enable_publishing = false,
  allow_symbol_change = true
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // If symbol prop is provided, use advanced chart widget
    if (symbol) {
      // Clean up any existing content first
      if (containerRef.current) {
        containerRef.current.innerHTML = ''
      }

      const script = document.createElement("script")
      script.src = "https://s3.tradingview.com/tv.js"
      script.async = true
      script.onload = () => {
        if (containerRef.current && (window as any).TradingView) {
          new (window as any).TradingView.widget({
            container_id: containerRef.current.id,
            autosize: true,
            symbol: symbol,
            interval: interval,
            timezone: timezone,
            theme: theme,
            style: style,
            locale: locale,
            toolbar_bg: toolbar_bg,
            enable_publishing: enable_publishing,
            allow_symbol_change: allow_symbol_change,
            height: height,
            width: '100%',
          })
        }
      }
      
      document.head.appendChild(script)

      return () => {
        if (script.parentNode) {
          script.parentNode.removeChild(script)
        }
      }
    } else {
      // Use ticker tape widget for default (no symbol)
      // Clean up any existing content first
      if (containerRef.current) {
        containerRef.current.innerHTML = ''
      }

      const script = document.createElement("script")
      script.src = "https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js"
      script.type = "text/javascript"
      script.async = true
      script.innerHTML = `
        {
        "symbols": [
          {
          "proName": "BSE:SENSEX",
          "title": "SENSEX"
          },
          {
          "proName": "BSE:RELIANCE",
          "title": "Reliance Industries"
          },
          {
          "proName": "BSE:TCS",
          "title": "Tata Consultancy Services"
          },
          {
          "proName": "BSE:HDFCBANK",
          "title": "HDFC Bank"
          },
          {
          "proName": "BSE:INFY",
          "title": "Infosys"
          },
          {
          "proName": "BSE:ICICIBANK",
          "title": "ICICI Bank"
          },
          {
          "proName": "BSE:SBIN",
          "title": "State Bank of India"
          }
        ],
        "colorTheme": "${theme}",
        "locale": "${locale}",
        "largeChartUrl": "",
        "isTransparent": true,
        "showSymbolLogo": true,
        "displayMode": "regular"
        }`
      
      if (containerRef.current) {
        containerRef.current.appendChild(script)
      }

      return () => {
        if (containerRef.current) {
          containerRef.current.innerHTML = ''
        }
      }
    }
  }, [symbol, theme, height, interval, toolbar_bg, style, locale, timezone, enable_publishing, allow_symbol_change])

  return (
    <div
      className="tradingview-widget-container w-full overflow-hidden"
      ref={containerRef}
      id={symbol ? `tradingview_${symbol.replace(':', '_')}` : 'tradingview-ticker'}
      style={{ height: symbol ? `${height}px` : 'auto' }}
      role="region"
      aria-label={symbol ? `Interactive price chart for ${symbol}` : 'Live market ticker tape'}
    >
      <div className="tradingview-widget-container__widget"></div>
    </div>
  )
}

export default memo(TradingViewWidget)
